import React, { FC } from 'react';
import { useSelector, TypedUseSelectorHook } from 'react-redux';

import {
  CertificationRatingStatus,
  ReviewStatus
} from 'types/graphql-api.generated';

import trans from 'common/tools/translations/trans';

import ReviewCardBam from 'website/components/UserOpinion/ReviewCardBam';
import WantToSeeButton from 'website/components/WantToSeeButton';
import ReviewLink from 'website/components/opinion/ReviewLink';
import StandaloneRating from 'website/components/push/Ratings/StandaloneRating';
import { State } from 'website/reducers';
import { getOpinionForEntity } from 'website/reducers/data/helper';

export type UserOpinionBannerProps = {
  entityId: string;
  title?: string;
  showTitle?: boolean;
  withSuggestions?: boolean;
  displayMode?: 'list' | 'default';
  hasShowtime?: boolean;
  onHasCompleteReviewChange?: (hasReview: boolean) => void;
};

type Opinion = {
  id: string;
  internalId?: number;
  status: ReviewStatus;
  rating?: number;
  review?: string;
  certificationRating?: CertificationRatingStatus | null;
};

const useTypedSelector: TypedUseSelectorHook<State> = useSelector;

const UserOpinionBanner: FC<UserOpinionBannerProps> = ({
  entityId,
  title,
  showTitle = true,
  withSuggestions = true,
  displayMode = 'default',
  hasShowtime = false,
  onHasCompleteReviewChange
}) => {
  const opinion = useTypedSelector(state =>
    getOpinionForEntity(entityId, state.data)
  ) as Opinion | null;

  const entity = useTypedSelector(state => state.data.all[entityId]);
  const entityTypename = entity?.typename ?? 'Movie';

  const hasRating = !!opinion?.rating;
  const hasReview = !!opinion?.review;
  const hasCompleteReview = hasRating && hasReview;

  if (onHasCompleteReviewChange) {
    onHasCompleteReviewChange(hasCompleteReview);
  }

  // Affichage spécial liste
  if (displayMode === 'list') {
    return (
      <div
        className={`bam-cell bam-rating-user user-opinion-banner list-mode ${hasCompleteReview ? 'user-with-review' : ''}`}
      >
        <StandaloneRating
          showDeleteRating={hasRating ? !(hasRating && hasReview) : undefined}
          entityId={entityId}
          showTitle={showTitle}
          title={title}
          withSuggestions={false}
          className="bam-cell"
          size="large"
          hasShowtime={hasShowtime && entityTypename === 'Movie'}
        />

        {!hasRating && (
          <WantToSeeButton
            entityId={entityId}
            className="button button-default-full button-md button icon icon-wish-full"
            addCellClass
          >
            {trans('bam.want-to-see')}
          </WantToSeeButton>
        )}

        {hasRating && (
          <ReviewLink
            label={
              hasReview
                ? trans('bam.edit-my-review')
                : trans('bam.write-review')
            }
            className="bam-cell bam-review"
            entityId={entityId}
            showLabel
          />
        )}
      </div>
    );
  }

  // Affichage classique
  return (
    <div
      className={`user-opinion-banner bam-cell default-mode ${hasCompleteReview ? 'user-with-review' : ''}`}
    >
      {!hasCompleteReview && (
        <>
          <StandaloneRating
            entityId={entityId}
            showTitle={showTitle}
            title={title}
            withSuggestions={withSuggestions}
            className="bam-cell bam-rating-user"
            size="large"
            hasShowtime={hasShowtime && entityTypename === 'Movie'}
          />
          <WantToSeeButton
            entityId={entityId}
            addCellClass
            className="button button-default-full button-md button icon icon-wish-full"
          >
            {trans('bam.want-to-see')}
          </WantToSeeButton>
          <ReviewLink className="bam-review" entityId={entityId} showLabel />
        </>
      )}

      {hasCompleteReview && opinion && (
        <ReviewCardBam
          id={opinion.id}
          internalId={entity?.legacyId ?? opinion.internalId ?? 0}
          entityId={entityId}
          title={title || ''}
          rating={opinion.rating ?? 0}
          review={opinion.review ?? ''}
          date={new Date()}
          typename={entityTypename}
          status={opinion.status}
          entityHref={`/movie/${entityId}`}
          certificationRating={opinion.certificationRating}
          hasShowtime={hasShowtime}
        />
      )}
    </div>
  );
};

export default UserOpinionBanner;
